"use client";

import { useEffect, useRef, useState } from "react";
import {
  FaHubspot,
  FaSalesforce,
  FaSlack,
  FaLinkedin,
  FaTrello,
  FaStripeS,
  FaDropbox,
  FaMailchimp,
  FaWhatsapp,
  FaIntercom,
  FaGoogleDrive,
} from "react-icons/fa";
import {
  SiGmail,
  SiAsana,
  SiNotion,
  SiCalendly,
  SiZendesk,
  SiQuickbooks,
} from "react-icons/si";
import { BiLogoMicrosoftTeams } from "react-icons/bi";

import { Reveal } from "@/components/ui/reveal";
import { CONNECTED_APP_COUNT } from "@/lib/integration-count";

interface App {
  name: string;
  icon: React.ReactNode;
  // The provider's status in the generated catalog (see
  // `integrationStatus()` in src/lib/integrations-catalog.ts): omitted for
  // a provider agents can act on today, "connect_only" for a working
  // connection no agent tool uses yet, "coming_soon" for a catalog
  // `coming_soon=True` tile. Every logo here must have a catalog entry; a
  // brand with no entry at all is not listed rather than shown
  // misleadingly. The parity test fails if this drifts from the catalog.
  status?: "connect_only" | "coming_soon";
}

// Exported for the catalog-parity test — see
// src/app/(marketing)/__tests__/app-integrations-catalog-parity.test.ts.
export const APPS: App[] = [
  { name: "HubSpot", icon: <FaHubspot style={{ color: "#ff7a59" }} /> },
  {
    name: "Salesforce",
    icon: <FaSalesforce style={{ color: "#00a1e0" }} />,
    status: "connect_only",
  },
  { name: "Slack", icon: <FaSlack style={{ color: "#e01e5a" }} /> },
  { name: "Gmail", icon: <SiGmail style={{ color: "#ea4335" }} /> },
  {
    name: "LinkedIn",
    icon: <FaLinkedin style={{ color: "#0a66c2" }} />,
    status: "connect_only",
  },
  {
    name: "Asana",
    icon: <SiAsana style={{ color: "#f06a6a" }} />,
    status: "connect_only",
  },
  {
    name: "Notion",
    icon: <SiNotion style={{ color: "#ffffff" }} />,
    status: "connect_only",
  },
  { name: "Calendly", icon: <SiCalendly style={{ color: "#006bff" }} /> },
  {
    name: "Teams",
    icon: <BiLogoMicrosoftTeams style={{ color: "#6264a7" }} />,
    status: "connect_only",
  },
  {
    name: "Trello",
    icon: <FaTrello style={{ color: "#0079bf" }} />,
    status: "coming_soon",
  },
  { name: "Stripe", icon: <FaStripeS style={{ color: "#635bff" }} /> },
  {
    name: "Dropbox",
    icon: <FaDropbox style={{ color: "#0061ff" }} />,
    status: "coming_soon",
  },
  {
    name: "Mailchimp",
    icon: <FaMailchimp style={{ color: "#ffe01b" }} />,
    status: "connect_only",
  },
  {
    name: "WhatsApp",
    icon: <FaWhatsapp style={{ color: "#25d366" }} />,
    status: "coming_soon",
  },
  {
    name: "Intercom",
    icon: <FaIntercom style={{ color: "#1f8ded" }} />,
    status: "connect_only",
  },
  {
    name: "Pipedrive",
    icon: (
      <svg viewBox="0 0 24 24" className="h-full w-full">
        <circle cx="12" cy="12" r="10" fill="#3b3f45" />
        <path
          d="M8 8v8M11 10v6M14 7v9M17 9v6"
          stroke="#4dc9f6"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    status: "connect_only",
  },
  {
    name: "Zendesk",
    icon: <SiZendesk style={{ color: "#78a300" }} />,
    status: "coming_soon",
  },
  {
    name: "Google Drive",
    icon: <FaGoogleDrive style={{ color: "#4285f4" }} />,
    status: "coming_soon",
  },
  {
    name: "DocuSign",
    icon: (
      <svg viewBox="0 0 24 24" className="h-full w-full">
        <rect x="2" y="2" width="20" height="20" rx="4" fill="#0076e1" />
        <path
          d="M7 12h10M12 7v10"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  { name: "QuickBooks", icon: <SiQuickbooks style={{ color: "#2ca01c" }} /> },
];

function CountUp({ target }: { target: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [val, setVal] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Reduced-motion is handled INSIDE the observer callback (async)
    // rather than as a synchronous setVal in the effect body — the
    // sync form is the React Compiler's cascading-render hazard. Side
    // benefit: the value now appears when the stat scrolls into view
    // in both modes, instead of being pre-set off-screen for
    // reduced-motion users.
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        if (reduceMotion) {
          setVal(target);
          return;
        }
        const start = performance.now();
        const duration = 1200;
        function tick(now: number) {
          const progress = Math.min((now - start) / duration, 1);
          // ease-out cubic
          const eased = 1 - Math.pow(1 - progress, 3);
          setVal(progress < 1 ? Math.floor(eased * target) : target);
          if (progress < 1) requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
      },
      { threshold: 0.3 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref} className="text-rl-accent tabular-nums">
      {val.toLocaleString()}
    </span>
  );
}

export function AppIntegrations() {
  return (
    <section className="py-24 md:py-28">
      <div className="mx-auto max-w-280 px-6 md:px-8">
        <Reveal className="mb-14 text-center">
          <h2 className="text-foreground text-[clamp(1.75rem,3.5vw,2.6rem)] leading-[1.15] font-extrabold tracking-[-0.035em]">
            Connect Raileon to{" "}
            <CountUp target={CONNECTED_APP_COUNT} /> apps
          </h2>
        </Reveal>

        <Reveal className="app-integrations-grid mx-auto grid max-w-225 grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-4 md:grid-cols-7">
          {APPS.map((app) => (
            <div
              key={app.name}
              className="app-integration-item group flex flex-col items-center gap-2 py-1 transition-opacity"
            >
              <div className="flex size-8 items-center justify-center text-[1.5rem] opacity-85 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:opacity-100 [&_svg]:h-full [&_svg]:w-full">
                {app.icon}
              </div>
              <span className="text-rl-text3 group-hover:text-rl-text2 text-center text-[0.65rem] leading-tight font-medium transition-colors duration-300">
                {app.name}
              </span>
              {app.status && (
                <span className="text-rl-text3/70 text-center text-[0.55rem] tracking-wide uppercase">
                  {app.status === "coming_soon" ? "Coming soon" : "Connect only"}
                </span>
              )}
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}
